<template>
  <div
    ref="rootRef"
    class="breadcrumbs"
  >
    <tm-breadcrumbs-list
      ref="measurementRef"
      :titled-breadcrumbs="titledBreadcrumbs"
      :collapse-middle-items="false"
      :truncate-last-item="false"
      :max-left-visible="maxLeftVisible"
      :max-right-visible="maxRightVisible"
      class="breadcrumbs__measurement"
    >
      <template
        v-if="$slots.label"
        v-slot:label="data"
      >
        <slot
          name="label"
          v-bind="data"
        />
      </template>
    </tm-breadcrumbs-list>
    <tm-breadcrumbs-list
      :titled-breadcrumbs="titledBreadcrumbs"
      :collapse-middle-items="collapseMiddleItems"
      :truncate-last-item="truncateLastItem"
      :max-left-visible="maxLeftVisible"
      :max-right-visible="maxRightVisible"
    >
      <template
        v-if="$slots.label"
        v-slot:label="data"
      >
        <slot
          name="label"
          v-bind="data"
        />
      </template>
    </tm-breadcrumbs-list>
  </div>
</template>

<script setup lang="ts">
import { get as lodashGet } from 'lodash-es'
import {
  useResizeObserver,
  computed,
  ref,
  useMutationObserver,
  type ComponentPublicInstance,
} from '@/composition/vue/compositionApi'
import type { Breadcrumb, Breadcrumbs } from '@/components/shared/types'
import { get as containerGet } from '@/core/container/container'
import type DefaultTitlerService from '@/services/route/titlers/defaultTitlerService'
import type { TranslationKey } from '@/services/types'
import type { Optional } from '@/types'
import TmBreadcrumbsList from '@/components/shared/TmBreadcrumbsList.vue'

/**
 * @param allowTruncateLastItem
 *  Example.
 *  Breadcrumbs is ['My contacts', 'Long name of contact']
 *
 *  If allowTruncateLastItem is false (default):
 *  then shows: "My co... > Long name of contact"
 *
 *  If allowTruncateLastItem is true:
 *  then shows: "My contacts > Long name of c..."
 */
const props = defineProps<{
  breadcrumbs: Breadcrumbs
  allowTruncateLastItem?: boolean
  maxLeftVisible?: number
  maxRightVisible?: number
}>()

const titlerService = containerGet<DefaultTitlerService>('DefaultTitlerService')

const getLabel = (breadcrumb: Breadcrumb): string => {
  if (breadcrumb.label) {
    return breadcrumb.label
  }
  const routeName = lodashGet(breadcrumb, ['route', 'name']) as TranslationKey | undefined
  return routeName ? titlerService.getPageTitle(routeName) : ''
}

const titledBreadcrumbs = computed(() =>
  props.breadcrumbs.map(
    (link) =>
      ({
        label: getLabel(link),
        route: link.route,
        onClick: link.onClick,
      }) as Breadcrumb,
  ),
)

const collapseMiddleItems = computed(() => {
  if (props.breadcrumbs.length <= 3) {
    return false
  }
  return !isFitContent.value
})

const truncateLastItem = computed(() => {
  return props.allowTruncateLastItem && props.breadcrumbs.length <= 3 && !isFitContent.value
})

const rootRef = ref<HTMLElement | null>(null)
const measurementRef = ref<ComponentPublicInstance | null>(null)
let lastValue: Optional<boolean>
const isFitContent = ref(false)
const checkFitContent = () => {
  if (!rootRef.value || !measurementRef.value?.$el) return

  const currentValue = rootRef.value.offsetWidth >= measurementRef.value.$el.offsetWidth
  if (lastValue === undefined || lastValue !== currentValue) {
    lastValue = currentValue
    isFitContent.value = currentValue
  }
}

useResizeObserver(
  () => rootRef.value,
  () => {
    checkFitContent()
  },
)

useMutationObserver(
  () => measurementRef.value?.$el,
  () => {
    checkFitContent()
  },
  {
    childList: true,
    subtree: true,
  },
)
</script>

<style lang="scss" scoped>
.breadcrumbs {
  max-width: 100%;
  position: relative;

  &__measurement {
    position: absolute;
    visibility: hidden;
    left: 0;
    top: 0;
    box-sizing: content-box;
  }
}
</style>
